// ✅ WritingFeedback.js — Final fixed version
import React, { useState } from "react";
import axios from "axios";
import {
  CheckCircle,
  Eye,
  EyeOff,
  FileText,
  AlertTriangle,
  Award,
  Target,
  MessageCircle,
  Star,
  TrendingUp,
  BookOpen,
  ArrowRight,
} from "lucide-react";

const WritingFeedback = ({
  isPlagiarized,
  currentScenario,
  handleNextStep,
  handleRetry,
  writingAttempt,
  selfAssessmentData = {},
  writingMode,
  cvData,
  scenario1Completed,
  scenario2Completed,
  isFirstAttemptFeedback = false,
  firstAttemptResults = null,
  isScenario2WritingCompleted = false,
  allowFinalResults = false,
  preventAutoFinalResults = false,
  currentScenarioData,
  apiGradingResults = null,
  attemptId, // ✅ nhận từ WritingSection để fetch self-assessment
}) => {
  const [showDetailedFeedback, setShowDetailedFeedback] = useState(false);
  const [showSelfAssessment, setShowSelfAssessment] = useState(false);
  const [dbSelfAssessment, setDbSelfAssessment] = useState(null);

  // ✅ Lấy sample writing từ DB (scenarios_writing.sample_response_note)
  const sampleWriting =
    currentScenarioData?.writing_task?.sampleWriting ||
    currentScenarioData?.writingTask?.sampleWriting ||
    currentScenarioData?.sampleWriting ||
    currentScenarioData?.sample_response_note || // 🆕 thêm dòng này
    null;


  // ✅ Fetch rubric self-assessment từ DB khi click
  const handleToggleSelfAssessment = async () => {
    if (!showSelfAssessment) {
      try {
        if (!attemptId) {
          console.warn("⚠️ No attemptId provided for fetching self-assessment");
          setShowSelfAssessment(true);
          return;
        }
        const res = await axios.get(`/test_attempts/review/${attemptId}`);
        setDbSelfAssessment(res.data.self_assessment);
      } catch (error) {
        console.error("❌ Error loading self-assessment:", error);
      }
    }
    setShowSelfAssessment(!showSelfAssessment);
  };

  // ================= HELPER UI ==================
  const getGradeColor = (grade) => {
    const map = {
      A: "text-green-700 bg-green-100",
      B: "text-blue-700 bg-blue-100",
      C: "text-yellow-700 bg-yellow-100",
      D: "text-orange-700 bg-orange-100",
      F: "text-red-700 bg-red-100",
    };
    return map[grade] || "text-gray-700 bg-gray-100";
  };

  const getCEFRColor = (level) => {
    const map = {
      C2: "text-emerald-700 bg-emerald-100",
      C1: "text-green-700 bg-green-100",
      B2: "text-blue-700 bg-blue-100",
      B1: "text-yellow-700 bg-yellow-100",
      A2: "text-orange-700 bg-orange-100",
      A1: "text-red-700 bg-red-100",
    };
    return map[level] || "text-gray-700 bg-gray-100";
  };

  const formatFeedbackCategory = (key) => {
    const names = {
      communicative_goals: "Communicative Goals",
      socio_cultural_context: "Socio-Cultural Context",
      position: "Position & Clarity",
      argumentation: "Argumentation",
      coherence_cohesion: "Coherence & Cohesion",
      source_processing: "Source Processing",
      lexical_grammar: "Vocabulary & Grammar",
    };
    return names[key] || key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const getCategoryIcon = (key) => {
    const icons = {
      communicative_goals: Target,
      socio_cultural_context: MessageCircle,
      position: Star,
      argumentation: TrendingUp,
      coherence_cohesion: BookOpen,
      source_processing: FileText,
      lexical_grammar: Award,
    };
    const Icon = icons[key] || FileText;
    return <Icon className="w-4 h-4" />;
  };

  // ✅ Self-assessment block
  const renderSelfAssessmentBlock = () => (
    <div className="mt-6 bg-purple-50 border border-purple-200 rounded-lg p-4">
      <h4 className="font-semibold text-purple-800 mb-2">🧩 Self-Assessment Integration</h4>
      <p className="text-sm text-purple-700 mb-2">
        You completed a self-assessment for this task.
        <button
          onClick={handleToggleSelfAssessment}
          className="text-purple-600 hover:underline ml-1"
        >
          Click here
        </button>{" "}
        to view your rubric data.
      </p>

      {showSelfAssessment && (
        <>
          {dbSelfAssessment ? (
            <div className="mt-3 bg-white p-3 rounded border border-purple-200 text-sm overflow-x-auto">
              <pre className="whitespace-pre-wrap text-gray-700">
                {JSON.stringify(dbSelfAssessment, null, 2)}
              </pre>
            </div>
          ) : (
            <p className="text-sm text-gray-600 italic">Loading rubric data...</p>
          )}

          {sampleWriting && (
            <div className="mt-4 bg-yellow-50 border border-yellow-200 p-3 rounded-lg">
              <h5 className="font-semibold text-yellow-800 mb-2">📘 Sample Writing</h5>
              <pre className="whitespace-pre-wrap text-gray-700 text-sm leading-relaxed">
                {sampleWriting}
              </pre>
            </div>
          )}
        </>
      )}
    </div>
  );

  // ✅ AI Feedback
  const renderAPIFeedback = () => {
    if (!apiGradingResults) return null;
    return (
      <div
        className={`border-l-4 p-6 rounded-lg ${isPlagiarized ? "bg-red-50 border-red-500" : "bg-blue-50 border-blue-500"
          }`}
      >
        <h4
          className={`font-semibold mb-4 text-lg ${isPlagiarized ? "text-red-800" : "text-blue-800"
            }`}
        >
          💬 AI Teacher Detailed Assessment
        </h4>

        {!isPlagiarized && (
          <div className="space-y-4">
            <div className="flex items-center justify-between bg-white rounded-lg p-4 shadow-sm border">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <Award className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-700">Overall Grade:</span>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${getGradeColor(
                      apiGradingResults.grade
                    )}`}
                  >
                    {apiGradingResults.grade}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Target className="w-5 h-5 text-indigo-600" />
                  <span className="font-medium text-gray-700">CEFR Level:</span>
                  <span
                    className={`px-3 py-1 rounded-full text-sm font-semibold ${getCEFRColor(
                      apiGradingResults.level
                    )}`}
                  >
                    {apiGradingResults.level}
                  </span>
                </div>
              </div>
            </div>

            {apiGradingResults.overall_comment && (
              <div className="bg-white rounded-lg p-4 shadow-sm border border-blue-200">
                <div className="flex items-start space-x-3">
                  <MessageCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h5 className="font-medium text-gray-800 mb-2">Overall Assessment</h5>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {apiGradingResults.overall_comment}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {apiGradingResults.feedback && (
              <div className="bg-white rounded-lg border border-blue-200 overflow-hidden">
                <button
                  onClick={() => setShowDetailedFeedback(!showDetailedFeedback)}
                  className="w-full p-4 text-left hover:bg-blue-50 flex items-center justify-between"
                >
                  <div className="flex items-center space-x-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <span className="font-medium text-gray-800">
                      Detailed Feedback by Criteria
                    </span>
                  </div>
                  {showDetailedFeedback ? (
                    <EyeOff className="w-4 h-4 text-gray-500" />
                  ) : (
                    <Eye className="w-4 h-4 text-gray-500" />
                  )}
                </button>

                {showDetailedFeedback && (
                  <div className="border-t border-blue-100 p-4 space-y-4">
                    {Object.entries(apiGradingResults.feedback).map(([key, feedback]) => (
                      <div key={key} className="bg-gray-50 rounded-lg p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          {getCategoryIcon(key)}
                          <h6 className="font-medium text-gray-800 text-sm">
                            {formatFeedbackCategory(key)}
                          </h6>
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">{feedback}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {Object.keys(selfAssessmentData).length > 0 && renderSelfAssessmentBlock()}
          </div>
        )}
      </div>
    );
  };

  // ✅ Render chính
  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-center mb-4">
        <CheckCircle className="w-6 h-6 text-green-600 mr-2" />
        <h3 className="text-lg font-semibold text-gray-800">
          {isFirstAttemptFeedback ? "Initial Assessment Complete" : "Writing Assessment Complete"}
        </h3>
      </div>

      {renderAPIFeedback()}

      <div className="flex justify-center mt-6">
        <button
          onClick={handleNextStep}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center shadow-md"
        >
          Writing Again
          <ArrowRight className="w-4 h-4 ml-2" />
        </button>
      </div>
    </div>
  );
};

export default WritingFeedback;
